import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Logo } from "@/components/landing/Logo";
import VideoManifesto from "@/components/landing/VideoManifesto";
import CarbonTrader from "@/components/landing/CarbonTrader";
import LiveSessions from "@/components/landing/LiveSessions";
import SharePanel from "@/components/landing/SharePanel";
import { HOME } from "@/constants/testIds";
import { AuthProvider } from "@/lib/AuthContext";
import LoginPage from "@/pages/LoginPage";
import AdminPage from "@/pages/AdminPage";
import UserAreaPage from "@/pages/UserAreaPage";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[+\d][\d\s.\-()]{6,}$/;

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
  }),
};

/* ============ NAV ============ */
const Nav = ({ waitlistCount }) => (
  <nav className="absolute top-0 left-0 right-0 z-30 px-6 lg:px-10 pt-5 sm:pt-6">
    <div className="mx-auto max-w-[1280px] flex items-center justify-between">
      <div data-testid={HOME.navLogo} className="flex items-center gap-2.5">
        <Logo size={24} />
        <span className="font-mono font-semibold text-[11px] tracking-[0.4em] text-white">
          L·I·F
        </span>
      </div>
      <div className="flex items-center gap-6">
        <Link
          to="/login"
          data-testid="nav-login-link"
          className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/60 hover:text-white transition-colors"
        >
          Accedi
        </Link>
        <a
          href="#iscriviti"
          data-testid={HOME.navCta}
          className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/80 hover:text-[color:var(--lif-accent)] transition-colors"
        >
          Iscriviti →
        </a>
      </div>
    </div>
  </nav>
);

/* ============ 01 · HERO WITH VIDEO ============ */
const HeroVideo = () => (
  <section
    data-testid={HOME.heroSection}
    className="section-dark min-h-screen relative flex flex-col justify-between pt-24 sm:pt-28 pb-16"
  >
    <Nav />

    <div className="mx-auto max-w-[1200px] w-full px-6 lg:px-10">
      <motion.div
        variants={fadeUp}
        custom={0}
        initial="hidden"
        animate="show"
        className="text-center mb-10 sm:mb-12"
      >
        <p className="eyebrow">Manifesto</p>
      </motion.div>

      <motion.div
        variants={fadeUp}
        custom={1}
        initial="hidden"
        animate="show"
      >
        <VideoManifesto embedded />
      </motion.div>

      <motion.div
        variants={fadeUp}
        custom={2}
        initial="hidden"
        animate="show"
        className="mt-14 sm:mt-20 text-center"
      >
        <div className="flex justify-center mb-6">
          <Logo size={42} />
        </div>
        <h1
          data-testid={HOME.heroTitle}
          className="font-display font-bold text-white leading-none"
          style={{ fontSize: "clamp(56px, 9vw, 140px)" }}
        >
          LIF<span className="text-[color:var(--lif-accent)]">.</span>
        </h1>
        <p
          data-testid={HOME.heroTagline}
          className="mt-4 font-mono text-white/70 text-xs sm:text-sm tracking-[0.4em] uppercase"
        >
          Libertà · Finanziaria
        </p>
        <p className="mt-6 font-body text-white/60 text-base sm:text-lg max-w-lg mx-auto leading-relaxed">
          Un percorso per costruire una vita straordinaria in un mercato
          gigantesco. Con metodo, non con fortuna.
        </p>

        <a href="#iscriviti" className="lif-btn mt-10">
          Iscriviti alla lista d'attesa
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 6h8M6 2l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
        </a>
      </motion.div>
    </div>
  </section>
);

/* ============ 02 · REGISTRATION FORM (light, prominent) ============ */
const RegistrationSection = ({ onJoined, waitlistCount }) => {
  // Read ?ref= from URL (case-insensitive) — auto-fills the referral field
  const urlRef = (() => {
    if (typeof window === "undefined") return "SAM";
    const p = new URLSearchParams(window.location.search);
    return (p.get("ref") || p.get("REF") || "SAM").toUpperCase().replace(/[^A-Z0-9]/g, "");
  })();

  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    referral_code: urlRef || "SAM",
  });
  const [state, setState] = useState("idle");
  const [successData, setSuccessData] = useState(null);
  const [errors, setErrors] = useState({});
  const [inviterName, setInviterName] = useState(null);

  // Validate the referral code exists (nice-to-have: show inviter name)
  useEffect(() => {
    if (!form.referral_code) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/referral/${encodeURIComponent(form.referral_code)}`
        );
        if (!cancel) {
          const label =
            data.referral_code === "SAM"
              ? "SAM · Codice fondatori"
              : [data.nome, data.cognome].filter(Boolean).join(" ") || data.referral_code;
          setInviterName(label);
        }
      } catch (_) {
        if (!cancel) setInviterName(null);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [form.referral_code]);

  const update = (key) => (e) => {
    let v = e.target.value;
    if (key === "referral_code") v = v.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setForm((f) => ({ ...f, [key]: v }));
  };

  const validate = () => {
    const errs = {};
    if (!form.nome.trim()) errs.nome = "Inserisci il tuo nome";
    if (!form.cognome.trim()) errs.cognome = "Inserisci il tuo cognome";
    if (!emailRegex.test(form.email)) errs.email = "Email non valida";
    if (!phoneRegex.test(form.telefono.trim()))
      errs.telefono = "Numero non valido";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Controlla i campi evidenziati.");
      return;
    }
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        email: form.email.trim().toLowerCase(),
        telefono: form.telefono.trim(),
        referral_code: form.referral_code || "SAM",
      });
      setSuccessData(data);
      setState("success");
      onJoined?.(data);
    } catch (err) {
      toast.error("Riprova tra un attimo.");
      setState("idle");
    }
  };

  return (
    <section
      id="iscriviti"
      data-testid={HOME.waitlistSection}
      className="section-light py-24 sm:py-32 px-6"
    >
      <div className="mx-auto max-w-3xl">
        <div className="text-center mb-12 sm:mb-16">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="eyebrow eyebrow-light"
          >
            Lista d'attesa · {String(waitlistCount).padStart(3, "0")}{" "}
            iscritti
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-5 font-display font-bold text-[color:var(--lif-light-fg)] leading-[0.9]"
            style={{ fontSize: "clamp(56px, 9vw, 128px)" }}
          >
            Iscriviti.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 font-body text-[color:var(--lif-light-fg-2)] text-base sm:text-lg max-w-xl mx-auto leading-relaxed"
          >
            Registrati per ricevere l'accesso prioritario al percorso LIF
            e tutte le novità in anteprima.
          </motion.p>
        </div>

        {state !== "success" ? (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.75, delay: 0.15 }}
            onSubmit={submit}
            className="bg-white border border-[color:var(--lif-light-line)] rounded-xl p-6 sm:p-10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormField
                label="Nome"
                testId={HOME.waitlistNomeInput}
                value={form.nome}
                onChange={update("nome")}
                placeholder="Mario"
                error={errors.nome}
                autoComplete="given-name"
              />
              <FormField
                label="Cognome"
                testId={HOME.waitlistCognomeInput}
                value={form.cognome}
                onChange={update("cognome")}
                placeholder="Rossi"
                error={errors.cognome}
                autoComplete="family-name"
              />
              <FormField
                label="Email"
                testId={HOME.waitlistEmailInput}
                value={form.email}
                onChange={update("email")}
                placeholder="mario.rossi@email.it"
                type="email"
                error={errors.email}
                autoComplete="email"
              />
              <FormField
                label="Cellulare"
                testId={HOME.waitlistTelefonoInput}
                value={form.telefono}
                onChange={update("telefono")}
                placeholder="+39 333 1234567"
                type="tel"
                error={errors.telefono}
                autoComplete="tel"
              />
            </div>

            {/* Referral code — pre-filled from ?ref= URL */}
            <div className="mt-5 pt-5 border-t border-[color:var(--lif-light-line)]">
              <FormField
                label={
                  inviterName
                    ? `Codice invito · Invitato da ${inviterName}`
                    : "Codice invito"
                }
                testId={HOME.waitlistReferralInput}
                value={form.referral_code}
                onChange={update("referral_code")}
                placeholder="SAM"
                error={null}
                autoComplete="off"
                mono
              />
            </div>

            <button
              type="submit"
              disabled={state === "loading"}
              data-testid={HOME.waitlistSubmit}
              className="lif-btn lif-btn-dark w-full mt-8 py-4 text-[13px]"
            >
              {state === "loading"
                ? "Registrazione in corso..."
                : "Iscriviti alla lista d'attesa"}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 7h12M8 2l5 5-5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>

            <p className="mt-5 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-[color:var(--lif-light-fg-2)]">
              Dati privati · Nessuno spam · Solo comunicazioni LIF
            </p>
          </motion.form>
        ) : (
          <motion.div
            data-testid={HOME.waitlistSuccess}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="bg-white border border-[color:var(--lif-light-line)] rounded-xl p-8 sm:p-12 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
          >
            <div className="text-center">
              <div className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--lif-light-fg-2)]">
                <span className="pulse-dot" /> Registrazione confermata
              </div>
              <h3
                className="mt-6 font-display font-bold text-[color:var(--lif-light-fg)] leading-tight"
                style={{ fontSize: "clamp(32px, 4.6vw, 56px)" }}
              >
                Benvenuto {successData?.nome || ""}.
              </h3>
              <p className="mt-4 font-body text-[color:var(--lif-light-fg-2)] max-w-md mx-auto">
                Ti abbiamo mandato una email di conferma con il tuo codice
                invito.
              </p>

              {/* Your personal referral code */}
              <div className="mt-8 border border-[color:var(--lif-light-line)] rounded-lg p-6 bg-[color:var(--lif-light-bg)]">
                <p className="eyebrow eyebrow-light">Il tuo codice invito</p>
                <p
                  data-testid={HOME.successReferralCode}
                  className="mt-3 font-mono font-bold text-[color:var(--lif-light-fg)] tracking-[0.24em]"
                  style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
                >
                  {successData?.referral_code}
                </p>
                <p className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[color:var(--lif-light-fg-2)]">
                  Posizione #{String(successData?.position ?? 0).padStart(3, "0")}
                </p>
              </div>
            </div>

            {/* Share panel */}
            <SharePanel
              code={successData?.referral_code}
              nome={successData?.nome}
            />
          </motion.div>
        )}
      </div>
    </section>
  );
};

const FormField = ({
  label,
  testId,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  autoComplete,
  mono = false,
}) => (
  <label className="block">
    <span className="eyebrow eyebrow-light block mb-2">{label}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      autoComplete={autoComplete}
      required={type !== "text" || !mono ? true : false}
      className={`w-full bg-white border rounded-md px-4 py-3 text-[color:var(--lif-light-fg)] placeholder:text-[color:var(--lif-light-fg-2)]/60 outline-none transition-colors ${
        mono ? "font-mono tracking-[0.14em] uppercase" : "font-body"
      } ${
        error
          ? "border-red-500/60 focus:border-red-500"
          : "border-[color:var(--lif-light-line)] focus:border-[color:var(--lif-light-fg)]"
      }`}
    />
    {error && (
      <span className="mt-1.5 block font-mono text-[10px] tracking-[0.16em] uppercase text-red-600">
        {error}
      </span>
    )}
  </label>
);

/* ============ 03 · EVENT (dark) ============ */
const EventSection = () => (
  <section
    id="evento"
    data-testid={HOME.eventSection}
    className="section-dark py-24 sm:py-32 px-6 border-t border-white/10"
  >
    <div className="mx-auto max-w-3xl text-center">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="eyebrow"
      >
        Evento di lancio
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.05 }}
        className="mt-5 font-display font-bold text-white leading-none"
        style={{ fontSize: "clamp(56px, 9vw, 128px)" }}
      >
        22 Ottobre
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="mt-4 font-mono text-white/60 text-sm sm:text-base tracking-[0.32em] uppercase"
      >
        Mercoledì · Dalle 16:30
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="mt-12 sm:mt-16 pt-12 border-t border-white/10 max-w-md mx-auto"
      >
        <p className="eyebrow mb-3">Dove</p>
        <p
          data-testid={HOME.eventVenue}
          className="font-display font-bold text-white text-2xl sm:text-3xl"
        >
          Hotel Cavalieri
        </p>
        <p className="mt-3 font-body text-white/70 leading-relaxed">
          Stradale Orbassano, 21<br />
          10064 Pinerolo (TO)
        </p>
        <a
          href="https://www.google.com/maps/search/?api=1&query=Hotel+Cavalieri+Pinerolo+Stradale+Orbassano+21"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 mt-6 font-mono text-[11px] tracking-[0.28em] uppercase text-white/60 hover:text-[color:var(--lif-accent)] transition-colors"
        >
          Apri in Maps →
        </a>
      </motion.div>
    </div>
  </section>
);

/* ============ FOOTER ============ */
const Footer = ({ waitlistCount }) => (
  <footer
    data-testid={HOME.footer}
    className="section-dark border-t border-white/10 py-8 px-6"
  >
    <div className="mx-auto max-w-[1280px] flex flex-col sm:flex-row items-center justify-between gap-3 font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">
      <div className="flex items-center gap-2.5">
        <Logo size={20} />
        <span>Libertà Finanziaria</span>
      </div>
      <span>
        {String(waitlistCount).padStart(3, "0")} iscritti · © MMXXVI
      </span>
    </div>
  </footer>
);

/* ============ PAGE ============ */
const Landing = () => {
  const [waitlistCount, setWaitlistCount] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/waitlist/stats`);
        if (!cancel) setWaitlistCount(data.total ?? 0);
      } catch (_) {}
    })();
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <div id="top">
      <HeroVideo />
      <CarbonTrader />
      <LiveSessions />
      <RegistrationSection
        waitlistCount={waitlistCount}
        onJoined={(d) => setWaitlistCount(d.position)}
      />
      <Footer waitlistCount={waitlistCount} />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0a0a0d",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#f4f4f5",
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/area" element={<UserAreaPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
